import React, { useState, useEffect, memo } from 'react';
import {
    Typography,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Box,
    Paper,
    Grid,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Select,
    MenuItem,
    CircularProgress,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import eventsApi from '../../../api/eventsApi/eventsApi';
import inspectionReportsApi, {
    InspectionReportDetailDto,
    InspectionReportAnswerDto,
    InspectionReportRiskAnswerDto,
} from '../../../api/eventsApi/inspectionReportsApi';
import { notifySpeechTranscriptionError } from '../../../services/speechTranscriptionNotify';

import { useSelector } from 'react-redux';

type QuestionFileSection = 'findings' | 'proposed';

function qaAttachmentIsFindings(f: { attachmentSection?: number | null }) {
    return f.attachmentSection == null || f.attachmentSection === 1;
}
function qaAttachmentIsProposed(f: { attachmentSection?: number | null }) {
    return f.attachmentSection === 2;
}

interface QuestionType {
    id: string;
    name: string;
}

interface QuestionCategory {
    id: string;
    name: string;
    isDisabled: boolean;
    index: number;
    questionType: QuestionType;
}

interface Question {
    id: string;
    text: string;
    isDisabled: boolean;
    userId: string | null;
    index: number;
    questionCategory: QuestionCategory;
    answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsResponse {
    questions: Question[];
}

const DEFAULT_CATEGORY_QUESTION_TYPE: QuestionType = {
    id: 'd5d3dd45-0a2e-457d-933b-c66a3853469f',
    name: 'Standart',
};

function mergeRiskCategoriesForInspectionReport(
    categoryMap: Map<string, QuestionCategory>,
    report: InspectionReportDetailDto,
    allPinQuestions: Question[],
): void {
    if (!report.riskAnswers?.length) return;
    const dedup = new Map<string, InspectionReportRiskAnswerDto>();
    for (const ra of report.riskAnswers) {
        if (!dedup.has(ra.questionCategoryId)) dedup.set(ra.questionCategoryId, ra);
    }
    const fallbackQt =
        allPinQuestions[0]?.questionCategory?.questionType ?? DEFAULT_CATEGORY_QUESTION_TYPE;
    for (const ra of dedup.values()) {
        if (categoryMap.has(ra.questionCategoryId)) continue;
        const live = allPinQuestions.find((q) => q.questionCategory.id === ra.questionCategoryId);
        if (live) {
            categoryMap.set(ra.questionCategoryId, live.questionCategory);
        } else {
            categoryMap.set(ra.questionCategoryId, {
                id: ra.questionCategoryId,
                name: ra.questionCategoryName?.trim() ? ra.questionCategoryName : 'Kategori',
                isDisabled: false,
                index: 999,
                questionType: fallbackQt,
            });
        }
    }
}

/** Denetim raporu: restrictQuestionIds null = henüz/yok; dizi (boş = hiç soru gösterme) */
function questionRestrictionSetForReport(
    inspectionReportId: string | undefined,
    restrictQuestionIds: string[] | null | undefined,
): Set<string> | null {
    if (!inspectionReportId) return null;
    if (restrictQuestionIds == null) return null;
    return new Set(restrictQuestionIds);
}

interface PinQuestionDetailsAnswer {
    id: string;
    yesNo: boolean;
    description: string | null;
    findings: string;
    proposedSolutions: string;
    fileAttachments: {
        id: string;
        fileName: string;
        attachmentSection?: number | null;
    }[];
    pinId: string;
}

interface PinQuestion {
    id: string;
    text: string;
    isDisabled: boolean;
    userId: string | null;
    index: number;
    questionCategory: QuestionCategory;
    answers: PinQuestionDetailsAnswer[];
}

interface PinQuestionsAndAnswersResponse {
    questions: PinQuestion[];
}

interface Answer {
    answer: string;
    description: string | null;
    findings: string | null;
    proposedSolutions: string | null;
    locked?: boolean;
    findingsFiles?: File[];
    proposedFiles?: File[];
    questionAnswerId?: string;
    fileAttachments?: {
        id: string;
        fileName: string;
        attachmentSection?: number | null;
    }[];
    fileAttachmentIdsToRemove?: string[];
}

const defaultAnswer: Answer = {
    answer: '',
    description: null,
    findings: null,
    proposedSolutions: null
};

interface FileAttachment {
    id: string;
    fileName: string;
}

interface FormAnswers {
    [key: string]: Answer;
}

interface ApiResponse<T> {
    items: T[];
}

interface RiskTableData {
    probabilityLevel: string;
    impactLevel: string;
    threatLevel: string;
    measureCoefficient: string;
    residualRiskLevel: string;
}

interface QuestionAnswer {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
    findingsFiles?: File[];
    proposedFiles?: File[];
    fileAttachmentIdsToRemove?: string[];
}

interface QuestionAnswerResponse {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
}

interface ChecklistModalProps {
    open: boolean;
    onClose: () => void;
    pinId: string;
    /** Gömülü mod (ChecklistPage accordion içinde) */
    embedded?: boolean;
    /** Gömülü modda doğrudan açılacak kategori ID */
    initialCategoryId?: string;
    /** Gömülü modda cevap kaydedildiğinde parent'ın refetch yapması için */
    onAnswersSaved?: () => void;
    /**
     * Geçmiş / anlık denetim raporu (InspectionReport). Set edilirse cevaplar bu rapordan gelir
     * ve kayıtlar Pin yerine rapora yazılır.
     */
    inspectionReportId?: string;
    /** Tamamlanmış rapor: salt okunur (Yeniden açmadan düzenlenemez). */
    inspectionReadOnly?: boolean;
    /** Kayıtlı PDF checklist görünümünde tamamlanmış raporda yalnızca bulgu/öneri düzenlenebilir. */
    allowCompletedFindingsEdit?: boolean;
    /** Sadece bu soru Id'leri gösterilir (ör. raporda cevaplananlar). */
    restrictQuestionIds?: string[] | null;
}

function inspectionAnswerToFormAnswer(
    a: InspectionReportAnswerDto,
    readOnly: boolean
): Answer {
    const gerek = (a.description || '').trim() === 'Gerek yok';
    let choice = '';
    if (gerek) choice = 'gerek_yok';
    else if (a.yesNo === true) choice = 'evet';
    else if (a.yesNo === false) choice = 'hayir';
    return {
        answer: choice,
        findings: a.findings || '',
        description: a.description || '',
        proposedSolutions: a.proposedSolutions || '',
        locked: readOnly,
        questionAnswerId: a.id,
        fileAttachments: (a.fileAttachments || []).map((f) => ({
            id: f.id,
            fileName: f.fileName ?? '',
        })),
    };
}

function applyReportRiskToModalState(
    report: InspectionReportDetailDto,
    setRiskAnswers: React.Dispatch<React.SetStateAction<Record<string, { beforeMeasures?: any; afterMeasures?: any }>>>,
    setRiskTablesData: React.Dispatch<React.SetStateAction<Record<string, { table1: RiskTableData; table2: RiskTableData }>>>,
    getEmptyRiskTableData: () => RiskTableData
) {
    const riskAnswersMap: {
        [key: string]: { beforeMeasures?: any; afterMeasures?: any };
    } = {};
    report.riskAnswers.forEach((ra) => {
        const catId = ra.questionCategoryId;
        if (!riskAnswersMap[catId]) riskAnswersMap[catId] = {};
        if (ra.isAfterMeasures) riskAnswersMap[catId].afterMeasures = ra;
        else riskAnswersMap[catId].beforeMeasures = ra;
    });
    setRiskAnswers(riskAnswersMap);
    setRiskTablesData((prev) => {
        const riskTablesMap: {
            [key: string]: { table1: RiskTableData; table2: RiskTableData };
        } = { ...prev };
        Object.keys(riskAnswersMap).forEach((catId) => {
            const before = riskAnswersMap[catId]?.beforeMeasures;
            const after = riskAnswersMap[catId]?.afterMeasures;
            riskTablesMap[catId] = {
                table1: before
                    ? {
                          probabilityLevel: before.probabilityLevel.toString(),
                          impactLevel: before.impactLevel.toString(),
                          threatLevel: before.threatLevel.toString(),
                          measureCoefficient: before.appliedMeasureCoefficient.toString(),
                          residualRiskLevel: before.residualRiskLevel.toString(),
                      }
                    : prev[catId]?.table1 || getEmptyRiskTableData(),
                table2: after
                    ? {
                          probabilityLevel: after.probabilityLevel.toString(),
                          impactLevel: after.impactLevel.toString(),
                          threatLevel: after.threatLevel.toString(),
                          measureCoefficient: after.appliedMeasureCoefficient.toString(),
                          residualRiskLevel: after.residualRiskLevel.toString(),
                      }
                    : prev[catId]?.table2 || getEmptyRiskTableData(),
            };
        });
        return riskTablesMap;
    });
}

async function upsertFindingsOnlyFromForm(
    inspectionReportId: string,
    questionId: string,
    answer: Answer
) {
    await inspectionReportsApi.upsertAnswer(inspectionReportId, questionId, {
        findings: answer.findings || null,
        proposedSolutions: answer.proposedSolutions || null,
    });
}

async function upsertInspectionAnswerFromForm(
    inspectionReportId: string,
    questionId: string,
    answer: Answer
) {
    const desc =
        answer.answer === 'gerek_yok'
            ? 'Gerek yok'
            : answer.description || null;
    const yesNo =
        answer.answer === 'evet'
            ? true
            : answer.answer === 'hayir'
              ? false
              : answer.answer === 'gerek_yok'
                ? false
                : null;
    await inspectionReportsApi.upsertAnswer(inspectionReportId, questionId, {
        yesNo,
        description: desc,
        findings: answer.findings || null,
        proposedSolutions: answer.proposedSolutions || null,
    });
}

interface AudioRecordingState {
    isRecording: boolean;
    mediaRecorder: MediaRecorder | null;
}

interface CategoryRiskAnswer {
    questionCategoryId: string;
    pinId: string;
    userId: string;
    probabilityLevel: number;
    impactLevel: number;
    threatLevel: number;
    appliedMeasureCoefficient: number;
    residualRiskLevel: number;
    isAfterMeasures: boolean;
}

interface CategoryRiskAnswerUpdate extends CategoryRiskAnswer {
    id: string;
}

interface CategoryRiskAnswerResponse extends CategoryRiskAnswerUpdate {
    // Eğer API'den dönen başka alanlar varsa buraya eklenebilir
}

interface QuestionCreate {
    text: string;
    userId: string;
    questionCategoryId: string;
    pinId: string;
}

interface QuestionCategoryCreate {
    name: string;
    userId: string;
    questionTypeId: string;
    pinId: string;
}

interface CategoryResponse {
    id: string;
    name: string;
    userId: string;
    questionTypeId: string;
}

interface QuestionAnswerUpdate {
    id: string;
    questionId: string;
    userId: string;
    yesNo: boolean;
    findings: string;
    pinId: string;
    proposedSolutions: string;
    description: string;
    findingsFiles?: File[];
    proposedFiles?: File[];
    fileAttachmentIdsToRemove?: string[];
}

const CategoryBox = memo(({
    name,
    onClick,
    questionCount,
    answeredCount,
    canDelete,
    onDelete
}: {
    name: string;
    onClick: () => void;
    questionCount: number;
    answeredCount: number;
    canDelete?: boolean;
    onDelete?: () => void;
}) => {
    const isAllAnswered = answeredCount === questionCount;

    return (
        <Paper
            elevation={2}
            sx={{
                p: 2,
                height: '100px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                transition: 'background-color 0.3s',
                position: 'relative'
            }}
            onClick={onClick}
        >
            {canDelete && onDelete && (
                <Tooltip title="Kategoriyi sil" placement="top">
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: '#b71c1c',
                            backgroundColor: 'rgba(255,255,255,0.8)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
                        }}
                    >
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            <Typography
                sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontStyle: 'italic',
                    color: isAllAnswered ? '#2e7d32' : '#666',
                    fontWeight: isAllAnswered ? 700 : 400,
                }}
            >
                {answeredCount}/{questionCount}
            </Typography>
            <Typography
                variant="subtitle1"
                align="center"
                sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#333'
                }}
            >
                {name}
            </Typography>
        </Paper>
    );
});

const PROBABILITY_OPTIONS = [
    { value: '1', label: '1 – Çok Nadiren' },
    { value: '2', label: '2 – Nadiren' },
    { value: '3', label: '3 – Bazen' },
    { value: '4', label: '4 – Sık Sık' },
    { value: '5', label: '5 – Her zaman' },
];

const IMPACT_OPTIONS = [
    { value: '1', label: '1 – İhmal Edilebilir' },
    { value: '2', label: '2 – Düşük' },
    { value: '3', label: '3 – Orta' },
    { value: '4', label: '4 – Yüksek' },
    { value: '5', label: '5 – Şiddetli' },
];

const MEASURE_OPTIONS = [
    { value: '1', label: '1 – Etkisiz' },
    { value: '0.75', label: '0.75 – Nadiren Etkili' },
    { value: '0.5', label: '0.5 – Kısmen Etkili' },
    { value: '0.25', label: '0.25 – Etkili' },
];

const FREE_OPTION_VALUE = '__free__';

const RiskDropdown = memo(({
    value,
    options,
    onChange,
    disabled,
}: {
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
    disabled?: boolean;
}) => {
    const isPreset = options.some(o => o.value === value);
    const [freeMode, setFreeMode] = useState(!isPreset && value !== '');

    return freeMode ? (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <TextField
                size="small"
                type="number"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                inputProps={{ min: 0, step: 'any' }}
                sx={{ width: 80 }}
                disabled={disabled}
            />
            <Button
                size="small" variant="text"
                onClick={() => { setFreeMode(false); onChange(''); }}
                sx={{ minWidth: 28, px: 0.5, fontSize: 11 }}
                disabled={disabled}
            >
                ✕
            </Button>
        </Box>
    ) : (
        <Select
            size="small"
            value={isPreset ? value : ''}
            displayEmpty
            onChange={(e) => {
                if (e.target.value === FREE_OPTION_VALUE) {
                    setFreeMode(true);
                    onChange('');
                } else {
                    onChange(e.target.value as string);
                }
            }}
            sx={{ minWidth: 130, fontSize: 13 }}
            disabled={disabled}
        >
            <MenuItem value="" disabled><em>Seçiniz</em></MenuItem>
            {options.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
            <MenuItem value={FREE_OPTION_VALUE} sx={{ fontStyle: 'italic', color: '#5A7A84' }}>
                + Serbest Değer
            </MenuItem>
        </Select>
    );
});

const RiskTable = memo(({
    data,
    onChange,
    backgroundColor,
    isSaving,
    readOnly,
}: {
    data: RiskTableData;
    onChange: (field: keyof RiskTableData, value: string) => void;
    backgroundColor: string;
    isSaving?: boolean;
    readOnly?: boolean;
}) => {
    const calculateThreatLevel = () => {
        const probability = parseFloat(data.probabilityLevel) || 0;
        const impact = parseFloat(data.impactLevel) || 0;
        return (probability * impact).toString();
    };

    const calculateResidualRisk = () => {
        const threat = parseFloat(calculateThreatLevel()) || 0;
        const coefficient = parseFloat(data.measureCoefficient) || 0;
        return (threat * coefficient).toString();
    };

    const getRiskLevel = () => {
        const residualRisk = parseFloat(calculateResidualRisk()) || 0;

        if (residualRisk < 3) {
            return { text: 'DÜŞÜK', bgColor: '#e0e0e0', textColor: '#000000' };
        } else if (residualRisk <= 10) {
            return { text: 'ORTA', bgColor: '#fff176', textColor: '#000000' };
        } else if (residualRisk < 20) {
            return { text: 'YÜKSEK', bgColor: '#ffb74d', textColor: '#000000' };
        } else {
            return { text: 'KRİTİK', bgColor: '#ef5350', textColor: '#FFFFFF' };
        }
    };

    const riskLevel = getRiskLevel();

    return (
        <Box>
            <TableContainer component={Paper} sx={{ mb: 2, backgroundColor }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Olasılık Seviyesi</TableCell>
                            <TableCell>Etki Seviyesi</TableCell>
                            <TableCell>Tehdit Seviyesi</TableCell>
                            <TableCell>Uygulanan Tedbirler Kat Sayısı</TableCell>
                            <TableCell>Kalıntı Risk Seviyesi</TableCell>
                            <TableCell>Risk ve Tehdit Seviyesi</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                <RiskDropdown
                                    value={data.probabilityLevel}
                                    options={PROBABILITY_OPTIONS}
                                    onChange={(v) => onChange('probabilityLevel', v)}
                                    disabled={!!readOnly || !!isSaving}
                                />
                            </TableCell>
                            <TableCell>
                                <RiskDropdown
                                    value={data.impactLevel}
                                    options={IMPACT_OPTIONS}
                                    onChange={(v) => onChange('impactLevel', v)}
                                    disabled={!!readOnly || !!isSaving}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography>{calculateThreatLevel()}</Typography>
                            </TableCell>
                            <TableCell>
                                <RiskDropdown
                                    value={data.measureCoefficient}
                                    options={MEASURE_OPTIONS}
                                    onChange={(v) => onChange('measureCoefficient', v)}
                                    disabled={!!readOnly || !!isSaving}
                                />
                            </TableCell>
                            <TableCell>
                                <Typography>{calculateResidualRisk()}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography sx={{
                                    backgroundColor: riskLevel.bgColor,
                                    color: riskLevel.textColor,
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    display: 'inline-block',
                                    fontWeight: 'bold'
                                }}>
                                    {riskLevel.text}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
});

const MicrophoneButton = memo(({
    onRecordingComplete,
    onTranscriptionComplete,
    disabled = false
}: {
    onRecordingComplete: (audioBlob: Blob) => void;
    onTranscriptionComplete: (text: string) => void;
    disabled?: boolean;
}) => {
    const [recordingState, setRecordingState] = useState<AudioRecordingState>({
        isRecording: false,
        mediaRecorder: null
    });
    const [isProcessing, setIsProcessing] = useState(false);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            const audioChunks: BlobPart[] = [];

            mediaRecorder.ondataavailable = (event) => {
                audioChunks.push(event.data);
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
                onRecordingComplete(audioBlob);

                // Stop all tracks in the stream
                stream.getTracks().forEach(track => track.stop());

                // Process the audio with our API
                try {
                    setIsProcessing(true);
                    const response = await eventsApi.getSpeechToText(audioBlob);
                    // Parse the response text to get only the text content
                    try {
                        const parsedResponse = JSON.parse(response.transcription);
                        onTranscriptionComplete(parsedResponse.text);
                    } catch (error) {
                        // If parsing fails, use the raw text
                        onTranscriptionComplete(response.transcription);
                    }
                } catch (error) {
                    console.error('Transcription error:', error);
                    notifySpeechTranscriptionError(error);
                } finally {
                    setIsProcessing(false);
                }
            };

            mediaRecorder.start();
            setRecordingState({ isRecording: true, mediaRecorder });
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };

    const stopRecording = () => {
        if (recordingState.mediaRecorder) {
            recordingState.mediaRecorder.stop();
            setRecordingState({ isRecording: false, mediaRecorder: null });
        }
    };

    return (
        <IconButton
            size="small"
            onClick={recordingState.isRecording ? stopRecording : startRecording}
            disabled={disabled || isProcessing}
            sx={{
                color: recordingState.isRecording ? '#f44336' :
                    isProcessing ? '#999' : '#7FB3C8',
                padding: '4px',
                '&:hover': {
                    backgroundColor: recordingState.isRecording ?
                        'rgba(244, 67, 54, 0.04)' :
                        'rgba(9, 65, 81, 0.04)'
                }
            }}
        >
            {recordingState.isRecording ?
                <MicOffIcon sx={{ fontSize: '18px' }} /> :
                <MicIcon sx={{ fontSize: '18px' }} />
            }
        </IconButton>
    );
});

const QuestionItem = memo(({
    question,
    index,
    answer,
    onAnswerChange,
    onDescriptionChange,
    onFindingsChange,
    onProposedSolutionsChange,
    onEditClick,
    onFileChange,
    onSaveEdit,
    isEditing,
    canDelete,
    onDelete,
    compactMode,
    onAddBulguClick,
    allowFindingsEdit,
}: {
    question: Question;
    index: number;
    answer: Answer;
    onAnswerChange: (id: string, value: string) => void;
    onDescriptionChange: (id: string, value: string) => void;
    onFindingsChange: (id: string, value: string) => void;
    onProposedSolutionsChange: (id: string, value: string) => void;
    onEditClick: (id: string) => void;
    onFileChange: (id: string, update: { section?: QuestionFileSection; localFiles?: File[]; fileAttachmentIdsToRemove?: string[] }) => void;
    onSaveEdit: (id: string) => void;
    isEditing: boolean;
    canDelete: boolean;
    onDelete: (id: string) => void;
    compactMode?: boolean;
    onAddBulguClick?: (questionId: string) => void | Promise<void>;
    allowFindingsEdit?: boolean;
}) => {
    const [expanded, setExpanded] = useState(false);
    const [isDownloading, setIsDownloading] = useState<{ [key: string]: boolean }>({});
    const [isGeneratingInsight, setIsGeneratingInsight] = useState(false);

    const processFileInput = (event: React.ChangeEvent<HTMLInputElement>, section: QuestionFileSection) => {
        if (event.target.files) {
            const newFiles = Array.from(event.target.files);
            const maxFileSize = 12 * 1024 * 1024;
            const validFiles: File[] = [];
            const invalidFiles: string[] = [];

            newFiles.forEach(file => {
                if (file.size > maxFileSize) {
                    invalidFiles.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
                } else {
                    validFiles.push(file);
                }
            });

            if (invalidFiles.length > 0) {
                console.warn('Files too large:', invalidFiles);
                alert(`Dosya boyutu 12MB'ı aşamaz:\n${invalidFiles.join('\n')}`);
            }

            if (validFiles.length > 0) {
                const prev = section === 'findings' ? (answer.findingsFiles || []) : (answer.proposedFiles || []);
                onFileChange(question.id, { section, localFiles: [...prev, ...validFiles] });
            }
        }
        event.target.value = '';
    };

    const handleFileRemove = (fileToRemove: File | { id: string; fileName: string }, section?: QuestionFileSection) => {
        if ('id' in fileToRemove) {
            const updatedFilesToRemove = [...(answer.fileAttachmentIdsToRemove || []), fileToRemove.id];
            onFileChange(question.id, { fileAttachmentIdsToRemove: updatedFilesToRemove });
        } else if (section === 'findings') {
            const updatedFiles = (answer.findingsFiles || []).filter(file => file !== fileToRemove);
            onFileChange(question.id, { section: 'findings', localFiles: updatedFiles });
        } else if (section === 'proposed') {
            const updatedFiles = (answer.proposedFiles || []).filter(file => file !== fileToRemove);
            onFileChange(question.id, { section: 'proposed', localFiles: updatedFiles });
        }
    };

    const handleFileDownload = async (fileId: string) => {
        try {
            setIsDownloading(prev => ({ ...prev, [fileId]: true }));
            const blob = await eventsApi.getFileAttachment(fileId);

            // Find the file attachment to get the correct filename
            const fileAttachment = answer.fileAttachments?.find(file => file.id === fileId);
            if (!fileAttachment) {
                throw new Error('File attachment not found');
            }

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = fileAttachment.fileName; // Use the actual filename from fileAttachment

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        } finally {
            setIsDownloading(prev => ({ ...prev, [fileId]: false }));
        }
    };

    if (compactMode) {
        const isGreen = answer?.answer === 'evet';
        const isOrange = answer?.answer === 'gerek_yok';
        const isRed = answer?.answer === 'hayir';
        return (
            <Paper sx={{ mb: 1.5, p: 1.5, borderRadius: 2, border: '1px solid #E4EFF3' }}>
                <Box
                    onClick={() => setExpanded(!expanded)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer', minHeight: 48 }}
                >
                    <Box sx={{ flexShrink: 0, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isRed ? (
                            <ErrorIcon sx={{ color: '#D93B2B', fontSize: 22 }} />
                        ) : isOrange ? (
                            <RemoveCircleOutlineIcon sx={{ color: '#E67E22', fontSize: 22 }} />
                        ) : isGreen ? (
                            <CheckCircleIcon sx={{ color: '#1FA35C', fontSize: 22 }} />
                        ) : (
                            <Box sx={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #C8DDE4', bgcolor: 'white' }} />
                        )}
                    </Box>
                    <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{question.text}</Typography>
                    <ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', color: '#5A7A84', transition: 'transform 0.2s' }} />
                </Box>
                {expanded && (
                    <Box
                        sx={{
                            mt: 1.5,
                            pt: 1.5,
                            borderTop: '1px solid #E4EFF3',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            width: '100%',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {answer.locked ? (
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#37474F' }}>
                                {isGreen && 'Seçim: Bulgu yok'}
                                {isRed && 'Seçim: Bulgu var'}
                                {isOrange && 'Seçim: Gerek yok'}
                                {!isGreen && !isRed && !isOrange && 'Seçim: —'}
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAnswerChange(question.id, 'evet');
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: 90,
                                        minHeight: 72,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        borderRadius: 2,
                                        border: 'none',
                                        cursor: 'pointer',
                                        bgcolor: isGreen ? '#C8E6C9' : '#E8F5E9',
                                        '&:hover': { bgcolor: '#C8E6C9' },
                                    }}
                                >
                                    <CheckCircleIcon sx={{ color: '#1B5E20', fontSize: 28 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1B5E20' }}>
                                        Bulgu Yok
                                    </Typography>
                                </Box>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAnswerChange(question.id, 'hayir');
                                        if (onAddBulguClick) void onAddBulguClick(question.id);
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: 90,
                                        minHeight: 72,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        borderRadius: 2,
                                        border: isRed ? '2px solid #C62828' : '2px solid #FFCDD2',
                                        cursor: 'pointer',
                                        bgcolor: isRed ? '#FFEBEE' : '#FFF5F5',
                                        '&:hover': { bgcolor: '#FFEBEE', borderColor: '#C62828' },
                                    }}
                                >
                                    <ErrorIcon sx={{ color: '#C62828', fontSize: 28 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#C62828' }}>
                                        Bulgu Var
                                    </Typography>
                                </Box>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onAnswerChange(question.id, 'gerek_yok');
                                    }}
                                    sx={{
                                        flex: 1,
                                        minWidth: 90,
                                        minHeight: 72,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                        borderRadius: 2,
                                        border: 'none',
                                        cursor: 'pointer',
                                        bgcolor: isOrange ? '#FDEBD0' : '#FFF8E1',
                                        '&:hover': { bgcolor: '#FDEBD0' },
                                    }}
                                >
                                    <RemoveCircleOutlineIcon sx={{ color: '#E67E22', fontSize: 28 }} />
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#E67E22' }}>
                                        Gerek Yok
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {(answer.description?.trim() ||
                            answer.findings?.trim() ||
                            answer.proposedSolutions?.trim() ||
                            (allowFindingsEdit && answer.locked && !!answer.answer)) && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25,
                                    width: '100%',
                                    bgcolor: '#FAFCFD',
                                    borderRadius: 1,
                                    p: 1.25,
                                    border: '1px solid #E4EFF3',
                                    position: 'relative',
                                }}
                            >
                                {allowFindingsEdit && answer.locked && !!answer.answer && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: -0.5 }}>
                                        <Button
                                            size="small"
                                            onClick={() => (isEditing ? onSaveEdit(question.id) : onEditClick(question.id))}
                                            sx={{
                                                minWidth: 88,
                                                color: isEditing ? '#fff' : '#094151',
                                                backgroundColor: isEditing ? '#094151' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: isEditing ? '#022631' : 'rgba(9, 65, 81, 0.08)',
                                                },
                                            }}
                                        >
                                            {isEditing ? 'Kaydet' : 'Düzenle'}
                                        </Button>
                                    </Box>
                                )}
                                {!!answer.description?.trim() && !isOrange && (
                                    <Box>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5A7A84', mb: 0.25 }}>
                                            Açıklama
                                        </Typography>
                                        <Typography sx={{ fontSize: 13, color: '#263238', whiteSpace: 'pre-wrap' }}>
                                            {answer.description}
                                        </Typography>
                                    </Box>
                                )}
                                {(isEditing && allowFindingsEdit) ? (
                                    <>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Bulgu"
                                            multiline
                                            minRows={2}
                                            value={answer.findings || ''}
                                            onChange={(e) => onFindingsChange(question.id, e.target.value)}
                                            sx={{ bgcolor: 'white' }}
                                        />
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Önerilen çözüm"
                                            multiline
                                            minRows={2}
                                            value={answer.proposedSolutions || ''}
                                            onChange={(e) => onProposedSolutionsChange(question.id, e.target.value)}
                                            sx={{ bgcolor: 'white' }}
                                        />
                                    </>
                                ) : (
                                    <>
                                        {!!answer.findings?.trim() && (
                                            <Box>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5A7A84', mb: 0.25 }}>
                                                    Bulgu
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, color: '#263238', whiteSpace: 'pre-wrap' }}>
                                                    {answer.findings}
                                                </Typography>
                                            </Box>
                                        )}
                                        {!!answer.proposedSolutions?.trim() && (
                                            <Box>
                                                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#5A7A84', mb: 0.25 }}>
                                                    Önerilen çözüm
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, color: '#263238', whiteSpace: 'pre-wrap' }}>
                                                    {answer.proposedSolutions}
                                                </Typography>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        );
    }

    return (
        <Paper sx={{ mb: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1, position: 'relative' }}>
            {(canDelete || answer.locked) && (
                <Box sx={{ position: 'absolute', right: 16, top: 12, display: 'flex', gap: 1, alignItems: 'center' }}>
                    {canDelete && (
                        <Tooltip title="Soruyu sil" placement="top">
                            <IconButton
                                size="small"
                                onClick={() => onDelete(question.id)}
                                sx={{
                                    color: '#b71c1c',
                                    '&:hover': { backgroundColor: 'rgba(183, 28, 28, 0.08)' }
                                }}
                            >
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}

                    {answer.locked && (
                        <Button
                            onClick={() => isEditing ? onSaveEdit(question.id) : onEditClick(question.id)}
                            sx={{
                                color: isEditing ? '#fff' : '#094151',
                                backgroundColor: isEditing ? '#094151' : 'transparent',
                                '&:hover': {
                                    backgroundColor: isEditing ? '#022631' : 'rgba(9, 65, 81, 0.08)',
                                    color: isEditing ? '#fff !important' : '#094151 !important',
                                }
                            }}
                        >
                            {isEditing ? 'Güncelle' : 'Düzenle'}
                        </Button>
                    )}
                </Box>
            )}

            <Typography sx={{ mb: 2, fontWeight: 500, pr: (canDelete || answer.locked) ? 8 : 0 }}>
                {index + 1}. {question.text}
            </Typography>

            <FormControl component="fieldset" sx={{ mb: 2 }} disabled={answer.locked && !isEditing}>
                <RadioGroup
                    row
                    value={answer?.answer || ''}
                    onChange={(e) => {
                        // Normal değişiklik durumunda değeri güncelle
                        onAnswerChange(question.id, e.target.value);
                    }}
                >
                    <FormControlLabel
                        value="evet"
                        control={<Radio />}
                        label="Evet"
                        onClick={(e) => {
                            // Eğer "evet" zaten seçiliyse, seçimi temizle
                            if (answer?.answer === 'evet') {
                                e.preventDefault();
                                onAnswerChange(question.id, '');
                            }
                        }}
                    />
                    <FormControlLabel
                        value="hayir"
                        control={<Radio />}
                        label="Hayır"
                        onClick={(e) => {
                            // Eğer "hayir" zaten seçiliyse, seçimi temizle
                            if (answer?.answer === 'hayir') {
                                e.preventDefault();
                                onAnswerChange(question.id, '');
                            }
                        }}
                    />
                </RadioGroup>
            </FormControl>

            <Grid sx={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Bulgular/Düşünceler"
                    variant="outlined"
                    value={answer?.findings || ''}
                    onChange={(e) => onFindingsChange(question.id, e.target.value)}
                    sx={{
                        mb: 1,
                        display: 'flex',
                        backgroundColor: '#fff',
                        borderRadius: '6px !important',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '64px',
                        '& .MuiInputBase-root': { height: '64px' },
                        '& .MuiOutlinedInput-input': { height: '100%', padding: '0 14px' }
                    }}
                    multiline
                    rows={2}
                    disabled={answer.locked && !isEditing}
                />
                <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                }}>
                    <MicrophoneButton
                        onRecordingComplete={(audioBlob) => { }}
                        onTranscriptionComplete={(text) => {
                            onFindingsChange(question.id, text);
                        }}
                        disabled={answer.locked && !isEditing}
                    />
                </Box>
            </Grid>

            <Box sx={{
                border: '1px dashed #94a3b8',
                borderRadius: 1,
                p: 2,
                mb: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                backgroundColor: '#fff',
                opacity: answer.locked && !isEditing ? 0.7 : 1,
            }}>
                <Typography variant="subtitle2" sx={{ color: '#094151', fontWeight: 600 }}>Bulgu görselleri</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        component="label"
                        disabled={answer.locked && !isEditing}
                        sx={{
                            width: '160px',
                            backgroundColor: '#196074',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#094151', color: '#fff', fontWeight: 600 },
                        }}
                    >
                        Dosya ekle
                        <input
                            type="file"
                            hidden
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.PDF,.JPG,.JPEG,.PNG"
                            onChange={(e) => processFileInput(e, 'findings')}
                            disabled={answer.locked && !isEditing}
                        />
                    </Button>
                    <Typography variant="body2" color="textSecondary">PDF, JPEG, PNG (max 12MB)</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {answer.fileAttachments?.map((file) => (
                        qaAttachmentIsFindings(file) && !(answer.fileAttachmentIdsToRemove || []).includes(file.id) && (
                            <Box
                                key={file.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#f0f0f0',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#e0e0e0' },
                                }}
                                onClick={() => handleFileDownload(file.id)}
                            >
                                <Typography variant="body2">
                                    {file.fileName}
                                    {isDownloading[file.id] && ' (İndiriliyor...)'}
                                </Typography>
                                {(!answer.locked || isEditing) && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFileRemove(file);
                                        }}
                                        sx={{ ml: 1 }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    ))}
                    {answer.findingsFiles?.map((file, index) => (
                        <Box
                            key={`nf-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: '#eef6ff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                            }}
                        >
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton size="small" onClick={() => handleFileRemove(file, 'findings')} sx={{ ml: 1 }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            </Box>

            <Typography
                onClick={async () => {
                    // Soru locked ise ve düzenle modunda değilse tıklanmasın
                    if (answer.locked && !isEditing) return;
                    if (isGeneratingInsight) return;

                    setIsGeneratingInsight(true);
                    try {
                        const response = await eventsApi.generateInsight({
                            question: question.text,
                            tespit: answer.findings || ''
                        });
                        onProposedSolutionsChange(question.id, response.insight);
                    } catch (error) {
                        console.error('AI öneri alınırken hata:', error);
                    } finally {
                        setIsGeneratingInsight(false);
                    }
                }}
                sx={{
                    mb: 1,
                    color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#196074',
                    cursor: (answer.locked && !isEditing) || isGeneratingInsight ? 'not-allowed' : 'pointer',
                    textDecoration: (answer.locked && !isEditing) ? 'none' : 'underline',
                    '&:hover': {
                        color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#094151'
                    },
                    opacity: (answer.locked && !isEditing) || isGeneratingInsight ? 0.5 : 1,
                    userSelect: 'none',
                    '&:active': {
                        color: (answer.locked && !isEditing) || isGeneratingInsight ? '#ccc' : '#022631'
                    },
                    pointerEvents: (answer.locked && !isEditing) || isGeneratingInsight ? 'none' : 'auto'
                }}
            >
                {isGeneratingInsight ? 'Öneri oluşturuluyor...' : 'Otomatik Öneri Oluştur'}
            </Typography>

            <Grid sx={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Öneriler"
                    variant="outlined"
                    value={answer?.proposedSolutions || ''}
                    onChange={(e) => onProposedSolutionsChange(question.id, e.target.value)}
                    sx={{
                        mb: 1,
                        display: 'flex',
                        backgroundColor: '#fff',
                        borderRadius: '6px !important',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '64px',
                        '& .MuiInputBase-root': { height: '64px' },
                        '& .MuiOutlinedInput-input': { height: '100%', padding: '0 14px' }
                    }}
                    multiline
                    rows={2}
                    disabled={answer.locked && !isEditing}
                />
                <Box sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 1
                }}>
                    <MicrophoneButton
                        onRecordingComplete={(audioBlob) => { }}
                        onTranscriptionComplete={(text) => {
                            onProposedSolutionsChange(question.id, text);
                        }}
                        disabled={answer.locked && !isEditing}
                    />
                </Box>
            </Grid>

            <Box sx={{
                border: '1px dashed #94a3b8',
                borderRadius: 1,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                backgroundColor: '#fff',
                opacity: answer.locked && !isEditing ? 0.7 : 1,
            }}>
                <Typography variant="subtitle2" sx={{ color: '#094151', fontWeight: 600 }}>Öneri görselleri</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        component="label"
                        disabled={answer.locked && !isEditing}
                        sx={{
                            width: '160px',
                            backgroundColor: '#196074',
                            color: '#fff',
                            '&:hover': { backgroundColor: '#094151', color: '#fff', fontWeight: 600 },
                        }}
                    >
                        Dosya ekle
                        <input
                            type="file"
                            hidden
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.PDF,.JPG,.JPEG,.PNG"
                            onChange={(e) => processFileInput(e, 'proposed')}
                            disabled={answer.locked && !isEditing}
                        />
                    </Button>
                    <Typography variant="body2" color="textSecondary">PDF, JPEG, PNG (max 12MB)</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {answer.fileAttachments?.map((file) => (
                        qaAttachmentIsProposed(file) && !(answer.fileAttachmentIdsToRemove || []).includes(file.id) && (
                            <Box
                                key={file.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    backgroundColor: '#f0f0f0',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    '&:hover': { backgroundColor: '#e0e0e0' },
                                }}
                                onClick={() => handleFileDownload(file.id)}
                            >
                                <Typography variant="body2">
                                    {file.fileName}
                                    {isDownloading[file.id] && ' (İndiriliyor...)'}
                                </Typography>
                                {(!answer.locked || isEditing) && (
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleFileRemove(file);
                                        }}
                                        sx={{ ml: 1 }}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        )
                    ))}
                    {answer.proposedFiles?.map((file, index) => (
                        <Box
                            key={`np-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                backgroundColor: '#f3e8ff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                            }}
                        >
                            <Typography variant="body2">{file.name}</Typography>
                            <IconButton size="small" onClick={() => handleFileRemove(file, 'proposed')} sx={{ ml: 1 }}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Paper>
    );
});

const AddCategoryBox = memo(({ onAddClick }: { onAddClick: () => void }) => (
    <Paper
        elevation={2}
        sx={{
            p: 2,
            height: '100px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: '#f5f5f5',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            transition: 'background-color 0.3s'
        }}
        onClick={onAddClick}
    >
        <AddIcon sx={{ fontSize: 32, color: '#666' }} />
        <Typography sx={{ mt: 1, color: '#666' }}>Yeni Kategori Ekle</Typography>
    </Paper>
));

const AddQuestionBox = memo(({ onAddClick }: { onAddClick: () => void }) => (
    <Paper
        elevation={2}
        sx={{
            p: 2,
            mt: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            backgroundColor: '#f5f5f5',
            '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            transition: 'background-color 0.3s'
        }}
        onClick={onAddClick}
    >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AddIcon sx={{ fontSize: 24, color: '#666' }} />
            <Typography sx={{ color: '#666' }}>Yeni Soru Ekle</Typography>
        </Box>
    </Paper>
));

const ChecklistModal: React.FC<ChecklistModalProps> = ({
    open,
    onClose,
    pinId,
    embedded = false,
    initialCategoryId,
    onAnswersSaved,
    inspectionReportId,
    inspectionReadOnly = false,
    allowCompletedFindingsEdit = false,
    restrictQuestionIds = null,
}) => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState<QuestionCategory[]>([]);
    const [questions, setQuestions] = useState<Question[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [answers, setAnswers] = useState<FormAnswers>({});
    const [loading, setLoading] = useState(true);
    const [riskTablesData, setRiskTablesData] = useState<{
        [key: string]: {
            table1: RiskTableData;
            table2: RiskTableData;
        }
    }>({});
    const [categoryQuestionCounts, setCategoryQuestionCounts] = useState<{ [key: string]: number }>({});
    const [userId, setUserId] = useState<string>('');
    const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newQuestionDialogOpen, setNewQuestionDialogOpen] = useState(false);
    const [newQuestionText, setNewQuestionText] = useState('');
    const [selectedCategoryName, setSelectedCategoryName] = useState<string>('');
    const [categoryAnsweredCounts, setCategoryAnsweredCounts] = useState<{ [key: string]: number }>({});
    const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
    const [riskAnswers, setRiskAnswers] = useState<{
        [key: string]: {
            beforeMeasures?: CategoryRiskAnswerResponse;
            afterMeasures?: CategoryRiskAnswerResponse;
        }
    }>({});
    const [riskTableSaving, setRiskTableSaving] = useState<'before' | 'after' | null>(null);

    // Yeni state'ler
    const [requiredQuestionDialogOpen, setRequiredQuestionDialogOpen] = useState(false);
    const [newlyCreatedCategoryId, setNewlyCreatedCategoryId] = useState<string>('');
    const [requiredQuestionText, setRequiredQuestionText] = useState('');

    // Dosya yönetimi için state'ler
    const [filesToRemove, setFilesToRemove] = useState<{ [questionId: string]: string[] }>({});

    // Category delete (admin/superadmin only)
    const [canDeleteCategories, setCanDeleteCategories] = useState(false);
    const [deleteCategoryDialogOpen, setDeleteCategoryDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<{ id: string; name: string } | null>(null);

    // Question delete (admin/superadmin only)
    const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
    const [questionToDelete, setQuestionToDelete] = useState<{ id: string; text: string } | null>(null);

    // Gömülü modda soru araması
    const [embeddedQuestionSearch, setEmbeddedQuestionSearch] = useState('');

    const DEFAULT_QUESTION_TYPE_ID = "d5d3dd45-0a2e-457d-933b-c66a3853469f";

    const sortQuestionsForCategory = (qs: any[]) => [...qs].sort((a, b) => (a.index ?? 999) - (b.index ?? 999));

    const storeUserId = useSelector((state: any) => state.auth.id);

    useEffect(() => {
        if (!storeUserId) {
            console.error('User ID not found');
            return;
        }
        setUserId(storeUserId);
    }, [storeUserId]);

    useEffect(() => {
        if (!open && !embedded) return;

        const checkDeleteAccess = async () => {
            if (inspectionReportId) {
                setCanDeleteCategories(false);
                return;
            }
            try {
                const user = await eventsApi.getUser();
                const superadminClaimId = '00000000-0000-0000-0000-000000000001';
                const adminClaimId = '00000000-0000-0000-0000-000000000002';
                setCanDeleteCategories(
                    user.operationClaimIds?.includes(superadminClaimId) ||
                    user.operationClaimIds?.includes(adminClaimId) ||
                    false
                );
            } catch (e) {
                console.error('Error checking delete access:', e);
                setCanDeleteCategories(false);
            }
        };

        checkDeleteAccess();
    }, [open, embedded, inspectionReportId]);

    // Modal açıldığında risk verilerini yükle
    useEffect(() => {
        const loadRiskData = async () => {
            if (inspectionReportId) return;
            if ((open || embedded) && pinId) {
                try {
                    const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
                    const riskAnswersMap: {
                        [key: string]: {
                            beforeMeasures?: any;
                            afterMeasures?: any;
                        }
                    } = {};

                    if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                        riskAnswersData.forEach((ra: any) => {
                            const catId = ra.questionCategoryId || ra.categoryName;
                            if (!catId) {
                                return;
                            }
                            if (!riskAnswersMap[catId]) {
                                riskAnswersMap[catId] = {};
                            }
                            // isAfterMeasures boolean olarak gelmeli
                            const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                            if (isAfter) {
                                riskAnswersMap[catId].afterMeasures = ra;
                            } else {
                                riskAnswersMap[catId].beforeMeasures = ra;
                            }
                        });
                    }
                    setRiskAnswers(riskAnswersMap);

                    // Risk tablosu verilerini riskAnswers'dan yükle
                    // Mevcut riskTablesData'yı koru
                    setRiskTablesData(prev => {
                        const riskTablesMap: {
                            [key: string]: {
                                table1: RiskTableData;
                                table2: RiskTableData;
                            }
                        } = { ...prev };

                        // Tüm kategoriler için risk verilerini yükle
                        Object.keys(riskAnswersMap).forEach(catId => {
                            const before = riskAnswersMap[catId]?.beforeMeasures;
                            const after = riskAnswersMap[catId]?.afterMeasures;

                            riskTablesMap[catId] = {
                                table1: before ? {
                                    probabilityLevel: before.probabilityLevel.toString(),
                                    impactLevel: before.impactLevel.toString(),
                                    threatLevel: before.threatLevel.toString(),
                                    measureCoefficient: before.appliedMeasureCoefficient.toString(),
                                    residualRiskLevel: before.residualRiskLevel.toString()
                                } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                                table2: after ? {
                                    probabilityLevel: after.probabilityLevel.toString(),
                                    impactLevel: after.impactLevel.toString(),
                                    threatLevel: after.threatLevel.toString(),
                                    measureCoefficient: after.appliedMeasureCoefficient.toString(),
                                    residualRiskLevel: after.residualRiskLevel.toString()
                                } : (prev[catId]?.table2 || getEmptyRiskTableData())
                            };
                        });

                        return riskTablesMap;
                    });
                } catch (error) {
                    console.error('Error loading risk data:', error);
                }
            }
        };
        loadRiskData();
    }, [open, embedded, pinId, inspectionReportId]);

    useEffect(() => {
        const fetchData = async () => {
            if (open || embedded) {
                console.log('ChecklistModal opened, fetching data...');
                try {
                    const [response, reportPrefetched] = await Promise.all([
                        eventsApi.getPinQuestionsAndAnswers(pinId),
                        inspectionReportId
                            ? inspectionReportsApi.getById(inspectionReportId)
                            : Promise.resolve(null as InspectionReportDetailDto | null),
                    ]);
                    const rq = questionRestrictionSetForReport(inspectionReportId, restrictQuestionIds);
                    let questionsList = response.questions;
                    if (rq) {
                        questionsList = questionsList.filter((q) => rq.has(q.id));
                    }

                    const categoryMap = questionsList.reduce((acc, question) => {
                        const category = question.questionCategory;
                        if (!acc.has(category.id)) {
                            acc.set(category.id, category);
                        }
                        return acc;
                    }, new Map<string, QuestionCategory>());

                    if (
                        inspectionReportId &&
                        reportPrefetched &&
                        reportPrefetched.pinId === pinId
                    ) {
                        mergeRiskCategoriesForInspectionReport(
                            categoryMap,
                            reportPrefetched,
                            response.questions,
                        );
                    }

                    const uniqueCategories = Array.from(categoryMap.values())
                        .filter((c) => c.name !== 'SONUÇ VE DEĞERLENDİRME')
                        .sort((a, b) => (a.index ?? 999) - (b.index ?? 999));

                    setCategories(uniqueCategories);

                    const counts = questionsList.reduce((acc, question) => {
                        const categoryId = question.questionCategory.id;
                        acc[categoryId] = (acc[categoryId] || 0) + 1;
                        return acc;
                    }, {} as { [key: string]: number });
                    setCategoryQuestionCounts(counts);

                    let formattedAnswers: FormAnswers = {};

                    if (inspectionReportId) {
                        const report =
                            reportPrefetched && reportPrefetched.pinId === pinId
                                ? reportPrefetched
                                : await inspectionReportsApi.getById(inspectionReportId);
                        formattedAnswers = report.answers.reduce((acc, a) => {
                            if (rq && !rq.has(a.questionId)) return acc;
                            acc[a.questionId] = inspectionAnswerToFormAnswer(a, inspectionReadOnly);
                            return acc;
                        }, {} as FormAnswers);
                        applyReportRiskToModalState(
                            report,
                            setRiskAnswers,
                            setRiskTablesData,
                            () => ({
                                probabilityLevel: '',
                                impactLevel: '',
                                threatLevel: '',
                                measureCoefficient: '',
                                residualRiskLevel: '',
                            })
                        );
                        const answeredCountsInsp = questionsList.reduce((acc, question) => {
                            const categoryId = question.questionCategory.id;
                            acc[categoryId] = (acc[categoryId] || 0) + 1;
                            return acc;
                        }, {} as { [key: string]: number });
                        setCategoryAnsweredCounts(answeredCountsInsp);
                    } else {
                        formattedAnswers = questionsList.reduce((acc, question) => {
                            if (question.answers && question.answers.length > 0) {
                                const answer = question.answers[0];
                                acc[question.id] = {
                                    answer: answer.description === 'Gerek yok' ? 'gerek_yok' : (answer.yesNo ? 'evet' : 'hayir'),
                                    findings: answer.findings,
                                    description: answer.description,
                                    proposedSolutions: answer.proposedSolutions,
                                    locked: true,
                                    questionAnswerId: answer.id,
                                    fileAttachments: answer.fileAttachments,
                                };
                            }
                            return acc;
                        }, {} as FormAnswers);
                        const answeredCounts = questionsList.reduce((acc, question) => {
                            if (question.answers && question.answers.length > 0) {
                                const categoryId = question.questionCategory.id;
                                acc[categoryId] = (acc[categoryId] || 0) + 1;
                            }
                            return acc;
                        }, {} as { [key: string]: number });
                        setCategoryAnsweredCounts(answeredCounts);
                    }

                    setAnswers(formattedAnswers);

                    console.log('Data fetched successfully');
                } catch (error) {
                    console.error('Error fetching data:', error);
                } finally {
                    setLoading(false);
                }
            } else if (!embedded) {
                // Modal kapandığında state'leri temizle (embedded modda unmount ile temizlenir)
                setSelectedCategory(null);
                setQuestions([]);
                setSelectedCategoryName('');
                setEditingQuestionId(null);
                setFilesToRemove({});
                setAnswers({});
                setLoading(true);
            }
        };

        fetchData();
    }, [open, embedded, pinId, inspectionReportId, inspectionReadOnly, restrictQuestionIds]);

    // Gömülü modda initialCategoryId ile kategoriyi otomatik aç
    useEffect(() => {
        if (!embedded || !initialCategoryId || loading || categories.length === 0) return;
        if (selectedCategory === initialCategoryId) return;
        handleCategoryClick(initialCategoryId);
    }, [embedded, initialCategoryId, loading, categories.length]);

    const handleConfirmDeleteCategory = async () => {
        if (!categoryToDelete) return;

        try {
            await eventsApi.deleteQuestionCategory(categoryToDelete.id);

            // Optimistic UI update; backend enforces Admin/Superadmin
            setCategories((prev) => prev.filter((c) => c.id !== categoryToDelete.id));
            setCategoryQuestionCounts((prev) => {
                const copy = { ...prev };
                delete copy[categoryToDelete.id];
                return copy;
            });
            setCategoryAnsweredCounts((prev) => {
                const copy = { ...prev };
                delete copy[categoryToDelete.id];
                return copy;
            });
        } catch (e) {
            console.error('Kategori silme hatası:', e);
            alert('Kategori silinemedi. Yetkiniz olmayabilir veya kategori silinemez durumda olabilir.');
        } finally {
            setDeleteCategoryDialogOpen(false);
            setCategoryToDelete(null);
        }
    };

    const handleConfirmDeleteQuestion = async () => {
        if (!questionToDelete || !selectedCategory) return;

        try {
            await eventsApi.deleteQuestion(questionToDelete.id);

            const wasAnswered = !!answers[questionToDelete.id]?.answer;

            setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete.id));
            setAnswers((prev) => {
                const copy = { ...prev };
                delete copy[questionToDelete.id];
                return copy;
            });

            setCategoryQuestionCounts((prev) => ({
                ...prev,
                [selectedCategory]: Math.max(0, (prev[selectedCategory] || 0) - 1),
            }));

            if (wasAnswered) {
                setCategoryAnsweredCounts((prev) => ({
                    ...prev,
                    [selectedCategory]: Math.max(0, (prev[selectedCategory] || 0) - 1),
                }));
            }
        } catch (e) {
            console.error('Soru silme hatası:', e);
            alert('Soru silinemedi. Yetkiniz olmayabilir veya soru silinemez durumda olabilir.');
        } finally {
            setDeleteQuestionDialogOpen(false);
            setQuestionToDelete(null);
        }
    };

    const handleCategoryClick = async (categoryId: string) => {
        if (!categoryId) return;

        setLoading(true);
        try {
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);
            const rq = questionRestrictionSetForReport(inspectionReportId, restrictQuestionIds);
            const categoryQuestions = response.questions.filter(
                (q) =>
                    q.questionCategory.id === categoryId &&
                    (rq == null || rq.has(q.id))
            );
            setQuestions(sortQuestionsForCategory(categoryQuestions) as unknown as Question[]);
            setSelectedCategory(categoryId);

            const category = categories.find(c => c.id === categoryId);
            if (category) {
                setSelectedCategoryName(category.name);
            }

            if (inspectionReportId) {
                setLoading(false);
                return;
            }

            // Risk cevaplarını yükle (mevcut state'i koruyarak güncelle)
            const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
            const riskAnswersMap: {
                [key: string]: {
                    beforeMeasures?: any;
                    afterMeasures?: any;
                }
            } = { ...riskAnswers }; // Mevcut state'i koru

            if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                riskAnswersData.forEach((ra: any) => {
                    const catId = ra.questionCategoryId || ra.categoryName;
                    if (!catId) {
                        return;
                    }
                    if (!riskAnswersMap[catId]) {
                        riskAnswersMap[catId] = {};
                    }
                    // isAfterMeasures boolean olarak gelmeli
                    const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                    if (isAfter) {
                        riskAnswersMap[catId].afterMeasures = ra;
                    } else {
                        riskAnswersMap[catId].beforeMeasures = ra;
                    }
                });
            }
            setRiskAnswers(riskAnswersMap);

            // Risk tablosu verilerini riskAnswers'dan yükle (mevcut state'i koruyarak güncelle)
            setRiskTablesData(prev => {
                const riskTablesMap: {
                    [key: string]: {
                        table1: RiskTableData;
                        table2: RiskTableData;
                    }
                } = { ...prev }; // Mevcut state'i koru

                Object.keys(riskAnswersMap).forEach(catId => {
                    const before = riskAnswersMap[catId]?.beforeMeasures;
                    const after = riskAnswersMap[catId]?.afterMeasures;

                    riskTablesMap[catId] = {
                        table1: before ? {
                            probabilityLevel: before.probabilityLevel.toString(),
                            impactLevel: before.impactLevel.toString(),
                            threatLevel: before.threatLevel.toString(),
                            measureCoefficient: before.appliedMeasureCoefficient.toString(),
                            residualRiskLevel: before.residualRiskLevel.toString()
                        } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                        table2: after ? {
                            probabilityLevel: after.probabilityLevel.toString(),
                            impactLevel: after.impactLevel.toString(),
                            threatLevel: after.threatLevel.toString(),
                            measureCoefficient: after.appliedMeasureCoefficient.toString(),
                            residualRiskLevel: after.residualRiskLevel.toString()
                        } : (prev[catId]?.table2 || getEmptyRiskTableData())
                    };
                });

                return riskTablesMap;
            });
        } catch (error) {
            console.error('Error fetching questions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBackClick = async () => {
        try {
            console.log('Going back to category list, clearing states...');

            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.description === 'Gerek yok' ? 'gerek_yok' : (answer.yesNo ? 'evet' : 'hayir'),
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);

            // Kategori başına cevaplanan soru sayılarını hesapla
            const answeredCounts = response.questions.reduce((acc, question) => {
                if (question.answers && question.answers.length > 0) {
                    const categoryId = question.questionCategory.id;
                    acc[categoryId] = (acc[categoryId] || 0) + 1;
                }
                return acc;
            }, {} as { [key: string]: number });

            // State'leri temizle
            setCategoryAnsweredCounts(answeredCounts);
            setSelectedCategory(null);
            setQuestions([]);
            setSelectedCategoryName('');
            setEditingQuestionId(null);
            setFilesToRemove({});
            setAnswers(formattedAnswers);

            console.log('States cleared successfully');
        } catch (error) {
            console.error('Error updating answered counts:', error);
        }
    };

    const handleAnswerChange = async (questionId: string, value: string) => {
        const prevAnswer = answers[questionId];
        if (prevAnswer?.locked) return;

        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], answer: value }
        }));

        // Gömülü modda evet/gerek_yok seçildiğinde hemen kaydet
        if (embedded && userId && (value === 'evet' || value === 'gerek_yok')) {
            try {
                if (inspectionReportId && !inspectionReadOnly) {
                    const merged: Answer = {
                        ...(prevAnswer || defaultAnswer),
                        answer: value,
                        findings: prevAnswer?.findings || '',
                        proposedSolutions: prevAnswer?.proposedSolutions || '',
                        description:
                            value === 'gerek_yok'
                                ? 'Gerek yok'
                                : (prevAnswer?.description ?? ''),
                    };
                    await upsertInspectionAnswerFromForm(inspectionReportId, questionId, merged);
                } else if (!inspectionReportId) {
                    const payload = {
                        questionId,
                        userId,
                        yesNo: value === 'evet' || value === 'gerek_yok',
                        findings: prevAnswer?.findings || '',
                        pinId,
                        proposedSolutions: prevAnswer?.proposedSolutions || '',
                        description: value === 'gerek_yok' ? 'Gerek yok' : (prevAnswer?.description || ''),
                    };
                    if (prevAnswer?.questionAnswerId) {
                        const fd = new FormData();
                        fd.append('Id', prevAnswer.questionAnswerId);
                        fd.append('QuestionId', questionId);
                        fd.append('UserId', userId);
                        fd.append('YesNo', value === 'gerek_yok' ? 'true' : (value === 'evet' ? 'true' : 'false'));
                        fd.append('Findings', prevAnswer?.findings || '');
                        fd.append('PinId', pinId);
                        fd.append('ProposedSolutions', prevAnswer?.proposedSolutions || '');
                        fd.append('Description', value === 'gerek_yok' ? 'Gerek yok' : (prevAnswer?.description || ''));
                        await eventsApi.updateQuestionAnswer(fd);
                    } else {
                        await eventsApi.saveQuestionAnswers(payload as any);
                    }
                }
                onAnswersSaved?.();
            } catch (e) {
                console.error('Embedded save error:', e);
            }
        }
    };

    const handleDescriptionChange = (questionId: string, value: string) => {
        if (answers[questionId]?.locked) return;
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], description: value }
        }));
    };

    const handleFindingsChange = (questionId: string, value: string) => {
        const answer = answers[questionId];
        const findingsEditActive = allowCompletedFindingsEdit && editingQuestionId === questionId;
        if (answer?.locked && !findingsEditActive) return;
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], findings: value }
        }));
    };

    const handleProposedSolutionsChange = (questionId: string, value: string) => {
        const answer = answers[questionId];
        const findingsEditActive = allowCompletedFindingsEdit && editingQuestionId === questionId;
        if (answer?.locked && !findingsEditActive) return;
        setAnswers(prev => ({
            ...prev,
            [questionId]: { ...prev[questionId], proposedSolutions: value }
        }));
    };

    const handleFileChange = (
        questionId: string,
        update: { section?: QuestionFileSection; localFiles?: File[]; fileAttachmentIdsToRemove?: string[] },
    ) => {
        if (answers[questionId]?.locked) return;
        setAnswers(prev => {
            const cur = prev[questionId];
            if (!cur) return prev;
            const next: Answer = { ...cur };
            if (update.fileAttachmentIdsToRemove !== undefined)
                next.fileAttachmentIdsToRemove = update.fileAttachmentIdsToRemove;
            if (update.localFiles !== undefined && update.section === 'findings')
                next.findingsFiles = update.localFiles;
            if (update.localFiles !== undefined && update.section === 'proposed')
                next.proposedFiles = update.localFiles;
            return { ...prev, [questionId]: next };
        });
        if (update.fileAttachmentIdsToRemove) {
            setFilesToRemove(prev => ({
                ...prev,
                [questionId]: update.fileAttachmentIdsToRemove!,
            }));
        }
    };

    const getEmptyRiskTableData = (): RiskTableData => ({
        probabilityLevel: '',
        impactLevel: '',
        threatLevel: '',
        measureCoefficient: '',
        residualRiskLevel: '',
    });

    const handleRiskTableChange = (questionId: string, tableIndex: number, field: keyof RiskTableData, value: string) => {
        setRiskTablesData(prev => ({
            ...prev,
            [questionId]: {
                ...prev[questionId] || {},
                ...(tableIndex === 1 ? {
                    table1: {
                        ...((prev[questionId] || {})?.table1 || getEmptyRiskTableData()),
                        [field]: value
                    },
                    table2: (prev[questionId] || {})?.table2 || getEmptyRiskTableData()
                } : {
                    table1: (prev[questionId] || {})?.table1 || getEmptyRiskTableData(),
                    table2: {
                        ...((prev[questionId] || {})?.table2 || getEmptyRiskTableData()),
                        [field]: value
                    }
                })
            }
        }));
    };

    const handleRiskTableSave = async (categoryId: string, isAfterMeasures: boolean) => {
        const tableData = riskTablesData[categoryId]?.[isAfterMeasures ? 'table2' : 'table1'];
        if (!tableData) {
            console.warn('No table data found for category:', categoryId, 'isAfterMeasures:', isAfterMeasures);
            return;
        }

        const hasData =
            (tableData.probabilityLevel ?? '').trim() !== '' ||
            (tableData.impactLevel ?? '').trim() !== '' ||
            (tableData.measureCoefficient ?? '').trim() !== '';

        if (!hasData) {
            return;
        }

        // Tehdit seviyesi hesaplama
        const probabilityLevel = parseFloat(tableData.probabilityLevel) || 0;
        const impactLevel = parseFloat(tableData.impactLevel) || 0;
        const threatLevel = probabilityLevel * impactLevel;

        // Kalıntı risk seviyesi hesaplama
        const appliedMeasureCoefficient = parseFloat(tableData.measureCoefficient) || 0;
        const residualRiskLevel = threatLevel * appliedMeasureCoefficient;

        const data: CategoryRiskAnswer = {
            questionCategoryId: categoryId,
            pinId,
            userId,
            probabilityLevel,
            impactLevel,
            threatLevel,
            appliedMeasureCoefficient,
            residualRiskLevel,
            isAfterMeasures: Boolean(isAfterMeasures)
        };

        try {
            const response = await eventsApi.saveCategoryRiskAnswers(data);
            setRiskAnswers(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId],
                    [isAfterMeasures ? 'afterMeasures' : 'beforeMeasures']: response
                }
            }));

            // Risk tablosu verilerini de güncelle
            setRiskTablesData(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId] || { table1: getEmptyRiskTableData(), table2: getEmptyRiskTableData() },
                    [isAfterMeasures ? 'table2' : 'table1']: {
                        probabilityLevel: probabilityLevel.toString(),
                        impactLevel: impactLevel.toString(),
                        threatLevel: threatLevel.toString(),
                        measureCoefficient: appliedMeasureCoefficient.toString(),
                        residualRiskLevel: residualRiskLevel.toString()
                    }
                }
            }));
        } catch (error) {
            console.error('Risk tablosu kaydedilirken hata:', error);
        }
    };

    const handleRiskTableUpdate = async (categoryId: string, isAfterMeasures: boolean) => {
        const tableData = riskTablesData[categoryId]?.[isAfterMeasures ? 'table2' : 'table1'];
        const existingAnswer = isAfterMeasures ?
            riskAnswers[categoryId]?.afterMeasures :
            riskAnswers[categoryId]?.beforeMeasures;

        if (!existingAnswer) {
            console.error('Existing answer not found for update');
            return;
        }

        // Eğer tableData yoksa, mevcut değerleri kullan
        const dataToUse = tableData || (existingAnswer ? {
            probabilityLevel: existingAnswer.probabilityLevel.toString(),
            impactLevel: existingAnswer.impactLevel.toString(),
            threatLevel: existingAnswer.threatLevel.toString(),
            measureCoefficient: existingAnswer.appliedMeasureCoefficient.toString(),
            residualRiskLevel: existingAnswer.residualRiskLevel.toString()
        } : getEmptyRiskTableData());

        // Tehdit seviyesi hesaplama
        const probabilityLevel = parseFloat(dataToUse.probabilityLevel) || 0;
        const impactLevel = parseFloat(dataToUse.impactLevel) || 0;
        const threatLevel = probabilityLevel * impactLevel;

        // Kalıntı risk seviyesi hesaplama
        const appliedMeasureCoefficient = parseFloat(dataToUse.measureCoefficient) || 0;
        const residualRiskLevel = threatLevel * appliedMeasureCoefficient;

        const data: CategoryRiskAnswerUpdate = {
            id: existingAnswer.id,
            questionCategoryId: categoryId,
            pinId,
            userId,
            probabilityLevel,
            impactLevel,
            threatLevel,
            appliedMeasureCoefficient,
            residualRiskLevel,
            isAfterMeasures: Boolean(isAfterMeasures)
        };

        try {
            const response = await eventsApi.updateCategoryRiskAnswers(data);
            // Güncellenmiş veriyi state'e kaydet
            setRiskAnswers(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId],
                    [isAfterMeasures ? 'afterMeasures' : 'beforeMeasures']: response
                }
            }));

            // Risk tablosu verilerini de güncelle
            setRiskTablesData(prev => ({
                ...prev,
                [categoryId]: {
                    ...prev[categoryId] || { table1: getEmptyRiskTableData(), table2: getEmptyRiskTableData() },
                    [isAfterMeasures ? 'table2' : 'table1']: {
                        probabilityLevel: probabilityLevel.toString(),
                        impactLevel: impactLevel.toString(),
                        threatLevel: threatLevel.toString(),
                        measureCoefficient: appliedMeasureCoefficient.toString(),
                        residualRiskLevel: residualRiskLevel.toString()
                    }
                }
            }));
        } catch (error) {
            console.error('Risk tablosu güncellenirken hata:', error);
        }
    };

    const persistRiskTable = async (categoryId: string, isAfterMeasures: boolean) => {
        if (!userId) {
            window.alert('Oturum bilgisi bulunamadı. Lütfen yeniden giriş yapın.');
            return;
        }
        const tableData = riskTablesData[categoryId]?.[isAfterMeasures ? 'table2' : 'table1'];
        const hasData =
            tableData &&
            ((tableData.probabilityLevel ?? '').trim() !== '' ||
                (tableData.impactLevel ?? '').trim() !== '' ||
                (tableData.measureCoefficient ?? '').trim() !== '');
        if (!hasData) {
            window.alert('Kaydetmek için Olasılık, Etki veya Tedbir katsayısı alanlarından en az birini seçin.');
            return;
        }
        const existing = isAfterMeasures
            ? riskAnswers[categoryId]?.afterMeasures
            : riskAnswers[categoryId]?.beforeMeasures;

        if (existing?.id) {
            await handleRiskTableUpdate(categoryId, isAfterMeasures);
        } else {
            await handleRiskTableSave(categoryId, isAfterMeasures);
        }
    };

    const handleRiskTableClear = async (categoryId: string, isAfterMeasures: boolean) => {
        const existing = isAfterMeasures
            ? riskAnswers[categoryId]?.afterMeasures
            : riskAnswers[categoryId]?.beforeMeasures;
        const serverId = existing?.id ? String(existing.id) : '';

        if (serverId) {
            const ok = window.confirm(
                'Bu risk tablosunun sunucudaki kaydını silmek istiyor musunuz? Kayıt kaldırıldıktan sonra raporda da görünmez.',
            );
            if (!ok) return;
            try {
                await eventsApi.deleteCategoryRiskAnswer(serverId, pinId);
            } catch (error) {
                console.error('Risk tablosu silinirken hata:', error);
                window.alert('Kayıt silinemedi. Ağ bağlantınızı kontrol edip tekrar deneyin.');
                return;
            }
            setRiskAnswers(prev => {
                const next = { ...prev };
                const slot = next[categoryId];
                if (!slot) return prev;
                const copy = { ...slot };
                if (isAfterMeasures) delete copy.afterMeasures;
                else delete copy.beforeMeasures;
                if (!copy.beforeMeasures && !copy.afterMeasures) delete next[categoryId];
                else next[categoryId] = copy;
                return next;
            });
        }

        setRiskTablesData(prev => ({
            ...prev,
            [categoryId]: {
                ...(prev[categoryId] || { table1: getEmptyRiskTableData(), table2: getEmptyRiskTableData() }),
                [isAfterMeasures ? 'table2' : 'table1']: getEmptyRiskTableData(),
            },
        }));
    };

    const handleSave = async () => {
        if (!userId) {
            console.error('User ID is required');
            return;
        }

        try {
            const unlockedAnswers = Object.entries(answers).filter(([_, answer]) => !answer.locked);
            console.log('Saving unlocked answers:', unlockedAnswers.length);

            const savePromises = unlockedAnswers.map(([questionId, answer]) => {
                console.log(`Saving answer for question ${questionId}:`, {
                    findingsFileCount: answer.findingsFiles?.length || 0,
                    proposedFileCount: answer.proposedFiles?.length || 0,
                    filesToRemove: answer.fileAttachmentIdsToRemove?.length || 0,
                    filesToRemoveIds: answer.fileAttachmentIdsToRemove || [],
                });

                setAnswers(prev => ({
                    ...prev,
                    [questionId]: { ...prev[questionId], locked: true }
                }));

                if (inspectionReportId && !inspectionReadOnly) {
                    return upsertInspectionAnswerFromForm(inspectionReportId, questionId, answer);
                }

                const questionAnswer: Omit<QuestionAnswer, 'id'> = {
                    questionId: questionId,
                    userId: userId,
                    yesNo: answer.answer === 'evet' || answer.answer === 'gerek_yok',
                    findings: answer.findings || '',
                    pinId: pinId,
                    proposedSolutions: answer.proposedSolutions || '',
                    description: answer.answer === 'gerek_yok' ? 'Gerek yok' : (answer.description || ''),
                    findingsFiles: answer.findingsFiles,
                    proposedFiles: answer.proposedFiles,
                    fileAttachmentIdsToRemove: answer.fileAttachmentIdsToRemove,
                };

                return eventsApi.saveQuestionAnswers(questionAnswer);
            });

            await Promise.all(savePromises);
            console.log('All answers saved successfully');

            // Risk tablolarını kaydet veya güncelle
            if (!inspectionReportId && selectedCategory) {
                const table1Data = riskTablesData[selectedCategory]?.table1;
                const table2Data = riskTablesData[selectedCategory]?.table2;
                const existingBeforeMeasures = riskAnswers[selectedCategory]?.beforeMeasures;
                const existingAfterMeasures = riskAnswers[selectedCategory]?.afterMeasures;

                // Öncesi tablo - eğer veri varsa (en az bir alan dolu olmalı)
                if (table1Data) {
                    const hasData = table1Data.probabilityLevel !== '' ||
                        table1Data.impactLevel !== '' ||
                        table1Data.measureCoefficient !== '';

                    if (hasData) {
                        if (existingBeforeMeasures) {
                            // Güncelleme - direkt güncelle
                            await handleRiskTableUpdate(selectedCategory, false);
                        } else {
                            // Yeni kayıt
                            await handleRiskTableSave(selectedCategory, false);
                        }
                    }
                }

                // Sonrası tablo - eğer veri varsa (en az bir alan dolu olmalı)
                if (table2Data) {
                    const hasData = table2Data.probabilityLevel !== '' ||
                        table2Data.impactLevel !== '' ||
                        table2Data.measureCoefficient !== '';

                    if (hasData) {
                        if (existingAfterMeasures) {
                            // Güncelleme - direkt güncelle
                            await handleRiskTableUpdate(selectedCategory, true);
                        } else {
                            // Yeni kayıt
                            await handleRiskTableSave(selectedCategory, true);
                        }
                    }
                }
            }

            if (inspectionReportId) {
                const report = await inspectionReportsApi.getById(inspectionReportId);
                const rq = questionRestrictionSetForReport(inspectionReportId, restrictQuestionIds);
                const nextAnswers = report.answers.reduce((acc, a) => {
                    if (rq && !rq.has(a.questionId)) return acc;
                    acc[a.questionId] = inspectionAnswerToFormAnswer(a, inspectionReadOnly);
                    return acc;
                }, {} as FormAnswers);
                setAnswers(nextAnswers);
                applyReportRiskToModalState(
                    report,
                    setRiskAnswers,
                    setRiskTablesData,
                    () => ({
                        probabilityLevel: '',
                        impactLevel: '',
                        threatLevel: '',
                        measureCoefficient: '',
                        residualRiskLevel: '',
                    })
                );
                return;
            }

            // Risk cevaplarını yeniden yükle
            try {
                const riskAnswersData = await eventsApi.getCategoryRiskAnswers(pinId);
                const riskAnswersMap: {
                    [key: string]: {
                        beforeMeasures?: any;
                        afterMeasures?: any;
                    }
                } = {};

                if (Array.isArray(riskAnswersData) && riskAnswersData.length > 0) {
                    riskAnswersData.forEach((ra: any) => {
                        const catId = ra.questionCategoryId || ra.categoryName;
                        if (!catId) {
                            return;
                        }
                        if (!riskAnswersMap[catId]) {
                            riskAnswersMap[catId] = {};
                        }
                        // isAfterMeasures boolean olarak gelmeli
                        const isAfter = ra.isAfterMeasures === true || ra.isAfterMeasures === 'true' || ra.isAfterMeasures === 1;
                        if (isAfter) {
                            riskAnswersMap[catId].afterMeasures = ra;
                        } else {
                            riskAnswersMap[catId].beforeMeasures = ra;
                        }
                    });
                }
                setRiskAnswers(riskAnswersMap);

                // Risk tablosu verilerini riskAnswers'dan yükle (mevcut state'i koruyarak güncelle)
                setRiskTablesData(prev => {
                    const riskTablesMap: {
                        [key: string]: {
                            table1: RiskTableData;
                            table2: RiskTableData;
                        }
                    } = { ...prev }; // Mevcut state'i koru

                    Object.keys(riskAnswersMap).forEach(catId => {
                        const before = riskAnswersMap[catId]?.beforeMeasures;
                        const after = riskAnswersMap[catId]?.afterMeasures;

                        riskTablesMap[catId] = {
                            table1: before ? {
                                probabilityLevel: before.probabilityLevel.toString(),
                                impactLevel: before.impactLevel.toString(),
                                threatLevel: before.threatLevel.toString(),
                                measureCoefficient: before.appliedMeasureCoefficient.toString(),
                                residualRiskLevel: before.residualRiskLevel.toString()
                            } : (prev[catId]?.table1 || getEmptyRiskTableData()),
                            table2: after ? {
                                probabilityLevel: after.probabilityLevel.toString(),
                                impactLevel: after.impactLevel.toString(),
                                threatLevel: after.threatLevel.toString(),
                                measureCoefficient: after.appliedMeasureCoefficient.toString(),
                                residualRiskLevel: after.residualRiskLevel.toString()
                            } : (prev[catId]?.table2 || getEmptyRiskTableData())
                        };
                    });

                    return riskTablesMap;
                });

            } catch (error) {
                console.error('Error reloading risk answers:', error);
            }

            // Yeni verileri yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.description === 'Gerek yok' ? 'gerek_yok' : (answer.yesNo ? 'evet' : 'hayir'),
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            // Kategori başına cevaplanan soru sayılarını güncelle
            const answeredCounts = response.questions.reduce((acc: { [key: string]: number }, question) => {
                if (question.answers && question.answers.length > 0) {
                    const categoryId = question.questionCategory.id;
                    acc[categoryId] = (acc[categoryId] || 0) + 1;
                }
                return acc;
            }, {});
            setCategoryAnsweredCounts(answeredCounts);

            // Kategorileri güncelle ve ana sayfaya dön
            const categoryMap = response.questions.reduce((acc, question) => {
                const category = question.questionCategory;
                if (!acc.has(category.id)) {
                    acc.set(category.id, category);
                }
                return acc;
            }, new Map<string, QuestionCategory>());

            const uniqueCategories = Array.from(categoryMap.values())
                .filter(c => c.name !== "SONUÇ VE DEĞERLENDİRME")
                .sort((a, b) => (a.index ?? 999) - (b.index ?? 999));

            setCategories(uniqueCategories);
            setSelectedCategory(null);
            setQuestions([]);
            setSelectedCategoryName('');

        } catch (error) {
            console.error('Cevaplar kaydedilirken hata oluştu:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined
            });
        }
    };

    const handleEditClick = (questionId: string) => {
        setEditingQuestionId(questionId);
    };

    const handleSaveEdit = async (questionId: string) => {
        try {
            const answer = answers[questionId];
            if (inspectionReportId && inspectionReadOnly && allowCompletedFindingsEdit) {
                await upsertFindingsOnlyFromForm(inspectionReportId, questionId, answer);
                setAnswers((prev) => ({
                    ...prev,
                    [questionId]: { ...prev[questionId], locked: true },
                }));
                setEditingQuestionId(null);
                onAnswersSaved?.();
                return;
            }
            if (inspectionReportId && !inspectionReadOnly) {
                await upsertInspectionAnswerFromForm(inspectionReportId, questionId, answer);
                setAnswers((prev) => ({
                    ...prev,
                    [questionId]: { ...prev[questionId], locked: true },
                }));
                setEditingQuestionId(null);
                onAnswersSaved?.();
                return;
            }

            if (!answer.questionAnswerId) {
                console.error('Question answer ID not found');
                return;
            }

            console.log(`Updating answer for question ${questionId}:`, {
                findingsFileCount: answer.findingsFiles?.length || 0,
                proposedFileCount: answer.proposedFiles?.length || 0,
                filesToRemove: answer.fileAttachmentIdsToRemove?.length || 0,
                filesToRemoveIds: answer.fileAttachmentIdsToRemove || [],
            });

            const formData = new FormData();
            formData.append('Id', answer.questionAnswerId);
            formData.append('QuestionId', questionId);
            formData.append('UserId', userId);
            formData.append('YesNo', (answer.answer === 'evet' || answer.answer === 'gerek_yok') ? 'true' : 'false');
            formData.append('Findings', answer.findings || '');
            formData.append('PinId', pinId);
            formData.append('ProposedSolutions', answer.proposedSolutions || '');
            formData.append('Description', answer.answer === 'gerek_yok' ? 'Gerek yok' : (answer.description || ''));

            const hasSplit =
                (answer.findingsFiles && answer.findingsFiles.length > 0) ||
                (answer.proposedFiles && answer.proposedFiles.length > 0);
            if (hasSplit) {
                answer.findingsFiles?.forEach(file => formData.append('FindingsFiles', file));
                answer.proposedFiles?.forEach(file => formData.append('ProposedSolutionsFiles', file));
            }

            // Add files to remove
            if (answer.fileAttachmentIdsToRemove) {
                answer.fileAttachmentIdsToRemove.forEach(fileId => {
                    formData.append('FileAttachmentIdsToRemove', fileId);
                });
            }

            await eventsApi.updateQuestionAnswer(formData);
            console.log(`Answer updated successfully for question ${questionId}`);

            // Yeni verileri yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            if (selectedCategory) {
                const categoryQuestions = response.questions.filter(
                    q => q.questionCategory.id === selectedCategory
                );
                setQuestions(sortQuestionsForCategory(categoryQuestions) as unknown as Question[]);
            }

            // Cevapları formatla
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.description === 'Gerek yok' ? 'gerek_yok' : (answer.yesNo ? 'evet' : 'hayir'),
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            setEditingQuestionId(null);

        } catch (error) {
            console.error('Soru güncellenirken hata oluştu:', error);
            console.error('Error details:', {
                message: error instanceof Error ? error.message : 'Unknown error',
                stack: error instanceof Error ? error.stack : undefined,
                questionId: questionId
            });
        }
    };

    const getInfoText = () => {
        if (!selectedCategory) {
            return `Risk Analiz Formu ${categories.length} bölümden oluşmaktadır.`;
        }
        return `Bu bölümde ${questions.length} soru bulunmaktadır.`;
    };

    const handleAddCategory = async () => {
        if (!newCategoryName.trim() || !userId) {
            return;
        }

        try {
            const categoryData: QuestionCategoryCreate = {
                name: newCategoryName.trim(),
                userId: userId,
                questionTypeId: DEFAULT_QUESTION_TYPE_ID,
                pinId: pinId
            };

            const newCategory = await eventsApi.saveQuestionCategory(categoryData) as CategoryResponse;
            setNewlyCreatedCategoryId(newCategory.id);

            // Dialog'u kapat ve zorunlu soru modalını aç
            setNewCategoryName('');
            setNewCategoryDialogOpen(false);
            setRequiredQuestionDialogOpen(true);

        } catch (error) {
            console.error('Kategori eklenirken hata oluştu:', error);
        }
    };

    const handleAddRequiredQuestion = async () => {
        if (!requiredQuestionText.trim() || !userId || !newlyCreatedCategoryId) {
            return;
        }

        try {
            const questionData: QuestionCreate = {
                text: requiredQuestionText.trim(),
                userId: userId,
                questionCategoryId: newlyCreatedCategoryId,
                pinId: pinId
            };

            await eventsApi.saveQuestion(questionData);

            // Modalı kapat ve state'i temizle
            setRequiredQuestionText('');
            setRequiredQuestionDialogOpen(false);

            // Kategorileri yeniden yükle
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Kategorileri ID'ye göre grupla ve tekrar edenleri filtrele
            const categoryMap = response.questions.reduce((acc, question) => {
                const category = question.questionCategory;
                if (!acc.has(category.id)) {
                    acc.set(category.id, category);
                }
                return acc;
            }, new Map<string, QuestionCategory>());

            const uniqueCategories = Array.from(categoryMap.values())
                .filter(c => c.name !== "SONUÇ VE DEĞERLENDİRME")
                .sort((a, b) => (a.index ?? 999) - (b.index ?? 999));

            setCategories(uniqueCategories);

            // Kategori sayılarını güncelle
            const counts = response.questions.reduce((acc, question) => {
                const categoryId = question.questionCategory.id;
                acc[categoryId] = (acc[categoryId] || 0) + 1;
                return acc;
            }, {} as { [key: string]: number });

            setCategoryQuestionCounts(counts);

            // Yeni eklenen kategorinin sorularını göster
            const categoryQuestions = response.questions.filter(
                q => q.questionCategory.id === newlyCreatedCategoryId
            );
            setQuestions(sortQuestionsForCategory(categoryQuestions) as unknown as Question[]);
            setSelectedCategory(newlyCreatedCategoryId);

            const newCategory = uniqueCategories.find(c => c.id === newlyCreatedCategoryId);
            if (newCategory) {
                setSelectedCategoryName(newCategory.name);
            }

            setNewlyCreatedCategoryId(''); // State'i temizle

        } catch (error) {
            console.error('Zorunlu soru eklenirken hata oluştu:', error);
        }
    };

    const handleAddQuestion = async () => {
        if (!newQuestionText.trim() || !userId || !selectedCategory) {
            return;
        }

        try {
            const questionData: QuestionCreate = {
                text: newQuestionText.trim(),
                userId: userId,
                questionCategoryId: selectedCategory,
                pinId: pinId
            };

            await eventsApi.saveQuestion(questionData);

            // Dialog'u kapat ve form'u temizle
            setNewQuestionText('');
            setNewQuestionDialogOpen(false);

            // Yeni soruları görebilmek için tekrar veriyi çek
            const response = await eventsApi.getPinQuestionsAndAnswers(pinId);

            // Seçili kategorinin sorularını filtrele ve güncelle
            const categoryQuestions = response.questions.filter(
                q => q.questionCategory.id === selectedCategory
            );
            setQuestions(sortQuestionsForCategory(categoryQuestions) as unknown as Question[]);

            // Cevapları formatla ve güncelle
            const formattedAnswers = response.questions.reduce((acc: FormAnswers, question) => {
                if (question.answers && question.answers.length > 0) {
                    const answer = question.answers[0];
                    acc[question.id] = {
                        answer: answer.description === 'Gerek yok' ? 'gerek_yok' : (answer.yesNo ? 'evet' : 'hayir'),
                        findings: answer.findings,
                        description: answer.description,
                        proposedSolutions: answer.proposedSolutions,
                        locked: true,
                        questionAnswerId: answer.id,
                        fileAttachments: answer.fileAttachments
                    };
                }
                return acc;
            }, {} as FormAnswers);
            setAnswers(formattedAnswers);

            // Kategori sayılarını güncelle
            const counts = response.questions.reduce((acc, question) => {
                const categoryId = question.questionCategory.id;
                acc[categoryId] = (acc[categoryId] || 0) + 1;
                return acc;
            }, {} as { [key: string]: number });
            setCategoryQuestionCounts(counts);

        } catch (error) {
            console.error('Soru eklenirken hata oluştu:', error);
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    backgroundColor: '#fff'
                }}>
                    <Typography>Sorular yükleniyor...</Typography>
                </Box>
            );
        }

        if (!selectedCategory) {
            if (embedded && initialCategoryId) {
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <Typography color="text.secondary">Sorular yükleniyor...</Typography>
                    </Box>
                );
            }
            return (
                <Box sx={{ flexGrow: 1, mb: 3 }}>
                    <Grid container spacing={2}>
                        {categories.map((category) => (
                            <Grid item xs={12} sm={6} md={3} key={category.id}>
                                <CategoryBox
                                    name={category.name}
                                    onClick={() => handleCategoryClick(category.id)}
                                    questionCount={categoryQuestionCounts[category.id] || 0}
                                    answeredCount={categoryAnsweredCounts[category.id] || 0}
                                    canDelete={canDeleteCategories && !inspectionReportId}
                                    onDelete={() => {
                                        setCategoryToDelete({ id: category.id, name: category.name });
                                        setDeleteCategoryDialogOpen(true);
                                    }}
                                />
                            </Grid>
                        ))}
                        {!inspectionReportId && (
                            <Grid item xs={12} sm={6} md={3}>
                                <AddCategoryBox onAddClick={() => setNewCategoryDialogOpen(true)} />
                            </Grid>
                        )}
                    </Grid>
                </Box>
            );
        }

        const qSearch = embedded ? embeddedQuestionSearch : '';
        const filteredQuestions = qSearch?.trim()
            ? questions.filter((q) => q.text?.toLocaleLowerCase('tr-TR').includes(qSearch.trim().toLocaleLowerCase('tr-TR')))
            : questions;

        return (
            <Box sx={{ mb: 3 }}>
                {embedded && (
                    <TextField
                        fullWidth
                        placeholder="Soru ara..."
                        value={embeddedQuestionSearch}
                        onChange={(e) => setEmbeddedQuestionSearch(e.target.value)}
                        size="small"
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
                    />
                )}
                {filteredQuestions.map((question, index) => (
                    <QuestionItem
                        key={question.id}
                        question={question}
                        index={index}
                        answer={answers[question.id] || defaultAnswer}
                        onAnswerChange={handleAnswerChange}
                        onDescriptionChange={handleDescriptionChange}
                        onFindingsChange={handleFindingsChange}
                        onProposedSolutionsChange={handleProposedSolutionsChange}
                        onEditClick={handleEditClick}
                        onFileChange={handleFileChange}
                        onSaveEdit={handleSaveEdit}
                        isEditing={editingQuestionId === question.id}
                        canDelete={canDeleteCategories && !inspectionReportId}
                        onDelete={(id) => {
                            const q = questions.find((x) => x.id === id);
                            setQuestionToDelete({ id, text: q?.text || 'Soru' });
                            setDeleteQuestionDialogOpen(true);
                        }}
                        compactMode={embedded}
                        allowFindingsEdit={allowCompletedFindingsEdit && inspectionReadOnly}
                        onAddBulguClick={
                            embedded && pinId && !inspectionReportId
                                ? async (qId) => {
                                      const ans = answers[qId];
                                      if (ans && userId) {
                                          try {
                                              const existingId = ans.questionAnswerId;
                                              const payload = {
                                                  questionId: qId,
                                                  userId,
                                                  yesNo: false,
                                                  findings: ans.findings || '',
                                                  pinId,
                                                  proposedSolutions: ans.proposedSolutions || '',
                                                  description: ans.description || '',
                                              };
                                              if (existingId) {
                                                  const fd = new FormData();
                                                  fd.append('Id', existingId);
                                                  fd.append('QuestionId', qId);
                                                  fd.append('UserId', userId);
                                                  fd.append('YesNo', 'false');
                                                  fd.append('Findings', ans.findings || '');
                                                  fd.append('PinId', pinId);
                                                  fd.append('ProposedSolutions', ans.proposedSolutions || '');
                                                  fd.append('Description', ans.description || '');
                                                  await eventsApi.updateQuestionAnswer(fd);
                                              } else {
                                                  await eventsApi.saveQuestionAnswers(payload as any);
                                              }
                                          } catch (e) {
                                              console.error('Save before navigate:', e);
                                          }
                                      }
                                      navigate(`/reports/${pinId}/checklist/bulgu/${qId}`);
                                  }
                                : undefined
                        }
                    />
                ))}

                <Box sx={{ mb: 4 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            mb: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            component="h3"
                            variant="h6"
                            sx={{
                                m: 0,
                                flex: '1 1 220px',
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#094151',
                            }}
                        >
                            Etkin Karşı Tedbirler Öncesi Risk ve Tehdit Seviyesi
                        </Typography>
                        {!inspectionReportId && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    disabled={riskTableSaving === 'before'}
                                    onClick={async () => {
                                        if (!selectedCategory) return;
                                        setRiskTableSaving('before');
                                        try {
                                            await persistRiskTable(selectedCategory, false);
                                        } finally {
                                            setRiskTableSaving(null);
                                        }
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: '#094151',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#022631' },
                                        '&:disabled': { backgroundColor: '#b0bec5', color: '#fff' },
                                    }}
                                    startIcon={riskTableSaving === 'before' ? <CircularProgress size={18} color="inherit" /> : undefined}
                                >
                                    Kaydet
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={riskTableSaving === 'before'}
                                    onClick={() => handleRiskTableClear(selectedCategory, false)}
                                    sx={{
                                        textTransform: 'none',
                                        border: '2px solid #094151',
                                        color: '#094151',
                                        backgroundColor: '#ffffff',
                                        '&:hover': {
                                            border: '2px solid #022631',
                                            color: '#022631',
                                            backgroundColor: 'rgba(9, 65, 81, 0.06)',
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#cfd8dc',
                                            color: '#90a4ae',
                                            backgroundColor: '#fafafa',
                                        },
                                    }}
                                >
                                    Temizle
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <RiskTable
                        data={riskTablesData[selectedCategory]?.table1 || getEmptyRiskTableData()}
                        onChange={(field, value) => handleRiskTableChange(selectedCategory, 1, field, value)}
                        backgroundColor="#bbdefb"
                        isSaving={riskTableSaving === 'before'}
                        readOnly={!!inspectionReportId}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            mt: 4,
                            mb: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography
                            component="h3"
                            variant="h6"
                            sx={{
                                m: 0,
                                flex: '1 1 220px',
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#094151',
                            }}
                        >
                            Etkin Karşı Tedbirler Sonrası Risk ve Tehdit Seviyesi
                        </Typography>
                        {!inspectionReportId && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                <Button
                                    variant="contained"
                                    disabled={riskTableSaving === 'after'}
                                    onClick={async () => {
                                        if (!selectedCategory) return;
                                        setRiskTableSaving('after');
                                        try {
                                            await persistRiskTable(selectedCategory, true);
                                        } finally {
                                            setRiskTableSaving(null);
                                        }
                                    }}
                                    sx={{
                                        textTransform: 'none',
                                        backgroundColor: '#094151',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#022631' },
                                        '&:disabled': { backgroundColor: '#b0bec5', color: '#fff' },
                                    }}
                                    startIcon={riskTableSaving === 'after' ? <CircularProgress size={18} color="inherit" /> : undefined}
                                >
                                    Kaydet
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={riskTableSaving === 'after'}
                                    onClick={() => handleRiskTableClear(selectedCategory, true)}
                                    sx={{
                                        textTransform: 'none',
                                        border: '2px solid #094151',
                                        color: '#094151',
                                        backgroundColor: '#ffffff',
                                        '&:hover': {
                                            border: '2px solid #022631',
                                            color: '#022631',
                                            backgroundColor: 'rgba(9, 65, 81, 0.06)',
                                        },
                                        '&.Mui-disabled': {
                                            borderColor: '#cfd8dc',
                                            color: '#90a4ae',
                                            backgroundColor: '#fafafa',
                                        },
                                    }}
                                >
                                    Temizle
                                </Button>
                            </Box>
                        )}
                    </Box>
                    <RiskTable
                        data={riskTablesData[selectedCategory]?.table2 || getEmptyRiskTableData()}
                        onChange={(field, value) => handleRiskTableChange(selectedCategory, 2, field, value)}
                        backgroundColor="#c8e6c9"
                        isSaving={riskTableSaving === 'after'}
                        readOnly={!!inspectionReportId}
                    />
                </Box>

                {!inspectionReportId && (
                    <AddQuestionBox onAddClick={() => setNewQuestionDialogOpen(true)} />
                )}

                {/* New Question Dialog */}
                <Dialog open={newQuestionDialogOpen} onClose={() => setNewQuestionDialogOpen(false)}>
                    <DialogTitle>Yeni Soru Ekle</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Soru"
                            fullWidth
                            multiline
                            rows={3}
                            value={newQuestionText}
                            onChange={(e) => setNewQuestionText(e.target.value)}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="contained"
                            onClick={() => setNewQuestionDialogOpen(false)}
                            sx={{
                                backgroundColor: '#757575',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#616161',
                                    color: '#ffffff'
                                }
                            }}
                        >
                            İptal
                        </Button>
                        <Button
                            onClick={handleAddQuestion}
                            variant="contained"
                            sx={{
                                backgroundColor: '#094151',
                                color: '#ffffff',
                                '&:hover': {
                                    backgroundColor: '#022631',
                                    color: '#ffffff'
                                }
                            }}
                        >
                            Ekle
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        );
    };

    if (embedded) {
        return (
            <Box sx={{ py: 1 }}>
                {renderContent()}
            </Box>
        );
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { maxHeight: '90vh' }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selectedCategory && (
                    <IconButton onClick={handleBackClick} size="small">
                        <ArrowBackIcon />
                    </IconButton>
                )}
                {selectedCategory ? selectedCategoryName : 'RİSK ANALİZ FORMU'}
            </DialogTitle>
            <DialogContent>
                <Typography sx={{ fontSize: "12px", marginBottom: "24px" }}>
                    {getInfoText()}
                </Typography>
                {renderContent()}
            </DialogContent>

            {/* Yeni Kategori Dialog'u */}
            <Dialog
                open={newCategoryDialogOpen}
                onClose={() => setNewCategoryDialogOpen(false)}
            >
                <DialogTitle>Yeni Kategori Ekle</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Kategori Adı"
                        fullWidth
                        value={newCategoryName}
                        onChange={(e) => setNewCategoryName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => setNewCategoryDialogOpen(false)}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        İptal
                    </Button>
                    <Button
                        onClick={handleAddCategory}
                        variant="contained"
                        sx={{
                            backgroundColor: '#094151',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#022631',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Kategori Silme Onay Dialog'u */}
            <Dialog
                open={deleteCategoryDialogOpen}
                onClose={() => {
                    setDeleteCategoryDialogOpen(false);
                    setCategoryToDelete(null);
                }}
            >
                <DialogTitle>Kategori Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        <b>{categoryToDelete?.name}</b> kategorisini silmek istediğinize emin misiniz?
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        Bu işlem sadece Admin/Süperadmin tarafından yapılabilir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setDeleteCategoryDialogOpen(false);
                            setCategoryToDelete(null);
                        }}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteCategory}
                        sx={{
                            backgroundColor: '#b71c1c',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#7f0000',
                                color: '#ffffff'
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Soru Silme Onay Dialog'u */}
            <Dialog
                open={deleteQuestionDialogOpen}
                onClose={() => {
                    setDeleteQuestionDialogOpen(false);
                    setQuestionToDelete(null);
                }}
            >
                <DialogTitle>Soru Sil</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mt: 1 }}>
                        Bu soruyu silmek istediğinize emin misiniz?
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        {questionToDelete?.text}
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: '13px', color: '#666' }}>
                        Bu işlem sadece Admin/Süperadmin tarafından yapılabilir.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setDeleteQuestionDialogOpen(false);
                            setQuestionToDelete(null);
                        }}
                        sx={{
                            backgroundColor: '#757575',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#616161',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Vazgeç
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteQuestion}
                        sx={{
                            backgroundColor: '#b71c1c',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#7f0000',
                                color: '#ffffff'
                            },
                        }}
                    >
                        Sil
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Zorunlu Soru Dialog'u */}
            <Dialog
                open={requiredQuestionDialogOpen}
                onClose={() => { }}
                disableEscapeKeyDown
            >
                <DialogTitle>Zorunlu Soru Ekle</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: '#666' }}>
                        Bu kategoriye en az bir soru eklemelisiniz.
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Soru"
                        fullWidth
                        multiline
                        rows={3}
                        value={requiredQuestionText}
                        onChange={(e) => setRequiredQuestionText(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleAddRequiredQuestion}
                        variant="contained"
                        sx={{
                            backgroundColor: '#094151',
                            color: '#ffffff',
                            '&:hover': {
                                backgroundColor: '#022631',
                                color: '#ffffff'
                            }
                        }}
                    >
                        Ekle
                    </Button>
                </DialogActions>
            </Dialog>

            <DialogActions sx={{ justifyContent: 'flex-end', display: 'flex', pr: 5 }}>
                {selectedCategory && (
                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            width: '120px', backgroundColor: '#094151', color: "#fff",
                            '&:hover': {
                                backgroundColor: '#022631', color: "#fff"
                            }
                        }}
                    >
                        Kaydet
                    </Button>
                )}
                {!selectedCategory && (
                    <Button onClick={onClose}
                        sx={{
                            width: '220px', backgroundColor: '#094151', color: "#fff",
                            borderWidth: '2px',
                            '&:hover': {
                                backgroundColor: '#022631', color: "#fff", fontWeight: 600
                            }
                        }}>Lokasyon Sayfasına Dön</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default memo(ChecklistModal);